import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import * as Sentry from '@sentry/vue';
import { MessageNotificationDto, SupportTicketCloseReasonEnum, SupportTicketDto, SupportTicketMessageDto } from '@/services/api-clients';
import { getXsrfHeaders } from '@/services/xsrf';

const HUB_PATH = '/hubs/driver-chat';

type MessageReceivedHandler = (message: SupportTicketMessageDto) => void;
type TicketClaimedHandler = (ticketId: number, employeeFullName: string) => void;
type TicketClosedHandler = (ticketId: number, reason: SupportTicketCloseReasonEnum) => void;
type TicketAssignedToMeHandler = (ticket: SupportTicketDto) => void;
type TicketDeletedHandler = (ticketId: number, employeeFullName: string) => void;
type OpenTicketsCountChangedHandler = (organisationId: number, count: number) => void;
type TypingStartedHandler = (ticketId: number, firstName: string) => void;
type NotificationCreatedHandler = (notification: MessageNotificationDto) => void;
type ReconnectedHandler = () => void;

class DriverChatHubClient {
  private connection: HubConnection | null = null;
  private joinedTicketIds = new Set<number>();
  private messageHandlers: MessageReceivedHandler[] = [];
  private claimedHandlers: TicketClaimedHandler[] = [];
  private closedHandlers: TicketClosedHandler[] = [];
  private assignedToMeHandlers: TicketAssignedToMeHandler[] = [];
  private deletedHandlers: TicketDeletedHandler[] = [];
  private openTicketsCountHandlers: OpenTicketsCountChangedHandler[] = [];
  private typingHandlers: TypingStartedHandler[] = [];
  private notificationCreatedHandlers: NotificationCreatedHandler[] = [];
  private reconnectedHandlers: ReconnectedHandler[] = [];

  isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  async start(): Promise<void> {
    if (this.connection && this.connection.state !== HubConnectionState.Disconnected) {
      return;
    }

    // In prod the hub must stay same-origin: VUE_APP_API_BASE_URL bakes the internal docker
    // host (http://api:8080) into the bundle, which the browser blocks as mixed content.
    const baseUrl = process.env.NODE_ENV === 'development'
      ? process.env.VUE_APP_API_BASE_URL
      : '/api';

    // Backend CORS uses AllowAnyOrigin() — combo with credentials is forbidden by CORS spec.
    // Token is delivered via accessTokenFactory (query param ?access_token=...), not cookie.
    this.connection = new HubConnectionBuilder()
      .withUrl(`${baseUrl}${HUB_PATH}`, {
        withCredentials: false,
        accessTokenFactory: async () => {
          const expressUrl = process.env.NODE_ENV === 'development'
            ? `${process.env.VUE_APP_EXPRESS_URL}/api/signalr-token`
            : '/api/signalr-token';
          const response = await fetch(expressUrl, { credentials: 'include', headers: getXsrfHeaders() });
          if (!response.ok) {
            throw new Error(`Cannot fetch SignalR token: HTTP ${response.status}`);
          }
          const { token } = await response.json();
          if (!token) {
            throw new Error('SignalR token endpoint returned empty token');
          }
          return token;
        },
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.on('MessageReceived', (message: SupportTicketMessageDto) => {
      this.messageHandlers.forEach((h) => h(message));
    });
    this.connection.on('TicketClaimed', (ticketId: number, employeeFullName: string) => {
      this.claimedHandlers.forEach((h) => h(ticketId, employeeFullName));
    });
    this.connection.on('TicketClosed', (ticketId: number, reason: SupportTicketCloseReasonEnum) => {
      this.closedHandlers.forEach((h) => h(ticketId, reason));
    });
    this.connection.on('TicketAssignedToMe', (ticket: SupportTicketDto) => {
      this.assignedToMeHandlers.forEach((h) => h(ticket));
    });
    this.connection.on('TicketDeleted', (ticketId: number, employeeFullName: string) => {
      this.deletedHandlers.forEach((h) => h(ticketId, employeeFullName));
    });
    this.connection.on('OpenTicketsCountChanged', (organisationId: number, count: number) => {
      this.openTicketsCountHandlers.forEach((h) => h(organisationId, count));
    });
    this.connection.on('TypingStarted', (ticketId: number, firstName: string) => {
      this.typingHandlers.forEach((h) => h(ticketId, firstName));
    });
    this.connection.on('NotificationCreated', (notification: MessageNotificationDto) => {
      this.notificationCreatedHandlers.forEach((h) => h(notification));
    });
    this.connection.onreconnected(() => {
      this.reconnectedHandlers.forEach((h) => h());
    });

    try {
      await this.connection.start();
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'signalr_start', source: 'driver-chat-hub' } });
      throw err;
    }
  }

  async stop(): Promise<void> {
    await this.connection?.stop();
    this.connection = null;
    this.joinedTicketIds.clear();
    this.messageHandlers = [];
    this.claimedHandlers = [];
    this.closedHandlers = [];
    this.assignedToMeHandlers = [];
    this.deletedHandlers = [];
    this.openTicketsCountHandlers = [];
    this.typingHandlers = [];
    this.notificationCreatedHandlers = [];
    this.reconnectedHandlers = [];
  }

  async joinTicket(ticketId: number): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      throw new Error('Hub not connected; call start() first');
    }
    if (this.joinedTicketIds.has(ticketId)) {
      return;
    }
    await this.connection.invoke('JoinTicket', ticketId);
    this.joinedTicketIds.add(ticketId);
  }

  async leaveTicket(ticketId: number): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      return;
    }
    if (!this.joinedTicketIds.has(ticketId)) {
      return;
    }
    await this.connection.invoke('LeaveTicket', ticketId);
    this.joinedTicketIds.delete(ticketId);
  }

  // Presence signal is best-effort: a disconnected hub must never break typing a message.
  async notifyTyping(ticketId: number): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      return;
    }
    await this.connection.invoke('NotifyTyping', ticketId);
  }

  onMessageReceived(handler: MessageReceivedHandler): () => void {
    this.messageHandlers.push(handler);
    return () => {
      this.messageHandlers = this.messageHandlers.filter((h) => h !== handler);
    };
  }

  onTicketClaimed(handler: TicketClaimedHandler): () => void {
    this.claimedHandlers.push(handler);
    return () => {
      this.claimedHandlers = this.claimedHandlers.filter((h) => h !== handler);
    };
  }

  onTicketClosed(handler: TicketClosedHandler): () => void {
    this.closedHandlers.push(handler);
    return () => {
      this.closedHandlers = this.closedHandlers.filter((h) => h !== handler);
    };
  }

  onTicketAssignedToMe(handler: TicketAssignedToMeHandler): () => void {
    this.assignedToMeHandlers.push(handler);
    return () => {
      this.assignedToMeHandlers = this.assignedToMeHandlers.filter((h) => h !== handler);
    };
  }

  onTicketDeleted(handler: TicketDeletedHandler): () => void {
    this.deletedHandlers.push(handler);
    return () => {
      this.deletedHandlers = this.deletedHandlers.filter((h) => h !== handler);
    };
  }

  onOpenTicketsCountChanged(handler: OpenTicketsCountChangedHandler): () => void {
    this.openTicketsCountHandlers.push(handler);
    return () => {
      this.openTicketsCountHandlers = this.openTicketsCountHandlers.filter((h) => h !== handler);
    };
  }

  onTypingStarted(handler: TypingStartedHandler): () => void {
    this.typingHandlers.push(handler);
    return () => {
      this.typingHandlers = this.typingHandlers.filter((h) => h !== handler);
    };
  }

  onNotificationCreated(handler: NotificationCreatedHandler): () => void {
    this.notificationCreatedHandlers.push(handler);
    return () => {
      this.notificationCreatedHandlers = this.notificationCreatedHandlers.filter((h) => h !== handler);
    };
  }

  onReconnected(handler: ReconnectedHandler): () => void {
    this.reconnectedHandlers.push(handler);
    return () => {
      this.reconnectedHandlers = this.reconnectedHandlers.filter((h) => h !== handler);
    };
  }
}

export const driverChatHub = new DriverChatHubClient();
